import { create } from 'zustand';

export const useShowStore = create((set, get) => ({
  show: null,
  cues: [],
  onAirStartTime: null,
  pendingOpCount: 0,
  collapsedGroups: new Set(),
  collapsingGroups: new Set(),
  expandingGroups: new Set(),
  // cueId -> startTimestamp for cues currently working through their action list.
  runningActions: {},

  // Derived getters
  get onAirCue() {
    return get().cues.find(c => c.status === 'on_air') || null;
  },

  get nextCue() {
    const cues = get().cues;
    const onAirIndex = cues.findIndex(c => c.status === 'on_air');
    if (onAirIndex === -1) {
      return cues.find(c => c.status === 'pending') || null;
    }
    return cues.slice(onAirIndex + 1).find(c => c.status === 'pending') || null;
  },

  // Mark a cue as running / done running its action sequence. On "done" we keep
  // the indicator up for a short floor so a very fast sequence still blinks
  // visibly rather than flashing for a millisecond.
  setCueActionsRunning: (cueId, running) => {
    if (!cueId) return;
    if (running) {
      set(state => ({ runningActions: { ...state.runningActions, [cueId]: Date.now() } }));
      return;
    }
    const startedAt = get().runningActions[cueId];
    if (!startedAt) return; // already cleared / never started
    const clear = () => set(state => {
      // Only clear if THIS run is still the active one — a new start during the
      // min-visible window sets a fresh timestamp and must not be wiped.
      if (state.runningActions[cueId] !== startedAt) return state;
      const next = { ...state.runningActions };
      delete next[cueId];
      return { runningActions: next };
    });
    const MIN_VISIBLE = 500;
    const elapsed = Date.now() - startedAt;
    if (elapsed < MIN_VISIBLE) setTimeout(clear, MIN_VISIBLE - elapsed);
    else clear();
  },

  // Actions
  setShow: (show) => set({ show }),

  setCues: (cues) => {
    const sorted = [...cues].sort((a, b) => a.order - b.order);
    set({ cues: sorted });
  },

  updateCue: (updatedCue) => {
    set(state => ({
      cues: state.cues.map(c => c.id === updatedCue.id ? { ...c, ...updatedCue } : c),
    }));
  },

  updateCueStatus: (cueId, status, extra = {}) => {
    set(state => {
      const newCues = state.cues.map(c =>
        c.id === cueId ? { ...c, status, ...extra } : c
      );

      // Auto-expand / auto-collapse GROUP cues
      const changedCue = newCues.find(c => c.id === cueId);
      let collapsedGroups = state.collapsedGroups;

      if (changedCue) {
        if (status === 'on_air') {
          // Expand the group when it or any of its children go on air
          const groupId = changedCue.cue_type === 'GROUP' ? changedCue.id : changedCue.parent_id;
          if (groupId && collapsedGroups.has(groupId)) {
            collapsedGroups = new Set(collapsedGroups);
            collapsedGroups.delete(groupId);
          }
        } else if ((status === 'complete' || status === 'skipped') && changedCue.parent_id) {
          // Collapse the group once all its children are done
          const siblings = newCues.filter(c => c.parent_id === changedCue.parent_id);
          const allDone = siblings.length > 0 && siblings.every(
            c => c.status === 'complete' || c.status === 'skipped'
          );
          if (allDone) {
            collapsedGroups = new Set(collapsedGroups);
            collapsedGroups.add(changedCue.parent_id);
          }
        }
      }

      return {
        cues: newCues,
        collapsedGroups,
        onAirStartTime: status === 'on_air' ? Date.now() : state.onAirStartTime,
      };
    });
  },

  addCue: (cue) => {
    set(state => {
      const exists = state.cues.some(c => c.id === cue.id);
      if (exists) return state;
      // If another cue occupies this order slot the backend shifted those cues up
      // but didn't broadcast the updates — fix the stale orders client-side.
      const hasConflict = state.cues.some(c => c.order === cue.order);
      const base = hasConflict
        ? state.cues.map(c => c.order >= cue.order ? { ...c, order: c.order + 1 } : c)
        : state.cues;
      const newCues = [...base, cue].sort((a, b) => a.order - b.order);
      return { cues: newCues };
    });
  },

  // Add a cue and patch a set of existing cues in one atomic update (single re-render)
  addCueAndPatchChildren: (newCue, childPatches) => {
    set(state => {
      if (state.cues.some(c => c.id === newCue.id)) return state;
      const hasConflict = state.cues.some(c => c.order === newCue.order);
      const base = hasConflict
        ? state.cues.map(c => c.order >= newCue.order ? { ...c, order: c.order + 1 } : c)
        : state.cues;
      const patchMap = new Map(childPatches.map(p => [p.id, p]));
      const patched = base.map(c => patchMap.has(c.id) ? { ...c, ...patchMap.get(c.id) } : c);
      return { cues: [...patched, newCue].sort((a, b) => a.order - b.order) };
    });
  },

  removeCue: (cueId) => {
    set(state => ({
      cues: state.cues.filter(c => c.id !== cueId),
    }));
  },

reorderCues: (orderedIds) => {
    set(state => {
      const cueMap = new Map(state.cues.map(c => [c.id, c]));
      const reordered = orderedIds
        .map((id, index) => {
          const cue = cueMap.get(id);
          return cue ? { ...cue, order: index } : null;
        })
        .filter(Boolean);
      return { cues: reordered };
    });
  },

  toggleGroupCollapse: (parentId) => {
    const isCurrentlyCollapsed = get().collapsedGroups.has(parentId);
    if (isCurrentlyCollapsed) {
      // Expanding: show children immediately, animate them in, then clear expanding flag
      set(state => {
        const nextCollapsed = new Set(state.collapsedGroups);
        nextCollapsed.delete(parentId);
        const nextExpanding = new Set(state.expandingGroups);
        nextExpanding.add(parentId);
        return { collapsedGroups: nextCollapsed, expandingGroups: nextExpanding };
      });
      setTimeout(() => {
        set(state => {
          const e = new Set(state.expandingGroups);
          e.delete(parentId);
          return { expandingGroups: e };
        });
      }, 200);
    } else {
      // Collapsing: mark as collapsing AND collapsed, remove collapsing flag after animation
      set(state => {
        const nextCollapsed = new Set(state.collapsedGroups);
        nextCollapsed.add(parentId);
        const nextCollapsing = new Set(state.collapsingGroups);
        nextCollapsing.add(parentId);
        return { collapsedGroups: nextCollapsed, collapsingGroups: nextCollapsing };
      });
      setTimeout(() => {
        set(state => {
          const c = new Set(state.collapsingGroups);
          c.delete(parentId);
          return { collapsingGroups: c };
        });
      }, 200);
    }
  },

  collapseAllGroups: () => {
    set(state => {
      const groupIds = state.cues.filter(c => c.cue_type === 'GROUP').map(c => c.id);
      return { collapsedGroups: new Set(groupIds) };
    });
  },

  resetSegmentTimer: () => set({ onAirStartTime: Date.now() }),

  incrementPendingOps: () => set(state => ({ pendingOpCount: state.pendingOpCount + 1 })),
  decrementPendingOps: () => set(state => ({ pendingOpCount: Math.max(0, state.pendingOpCount - 1) })),

  resetAllCues: () => set(state => ({
    cues: state.cues.map(c => ({ ...c, status: 'pending', actual_duration: null })),
    onAirStartTime: null,
  })),

  resetStore: () => set({
    show: null,
    cues: [],
    onAirStartTime: null,
    pendingOpCount: 0,
    collapsedGroups: new Set(),
    collapsingGroups: new Set(),
    expandingGroups: new Set(),
  }),
}));
